import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../layout/TopBar';
import { useHGS } from '../context/HGSContext';
import { ORG_TREE, lineageForDept, resolveDeptId } from '../data/orgTree';
import {
  Building2, ClipboardList, Inbox, CheckCircle2, AlertTriangle, Clock,
  ChevronRight, ShieldCheck, FileSignature, Layers, Send, Lock, ArrowUpRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const MODULE_STYLES = {
  fraud: { label: 'Fraud', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', icon: ShieldCheck },
  pcc:   { label: 'PCC',   bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', icon: ClipboardList },
  dlc:   { label: 'DLC',   bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: FileSignature },
};

const STATUS_TONE = {
  Submitted:    { label: 'Awaiting Review', cls: 'bg-blue-100 text-blue-800' },
  UnderReview:  { label: 'In Review',       cls: 'bg-amber-100 text-amber-800' },
  Approved:     { label: 'Forwarded to OH', cls: 'bg-emerald-100 text-emerald-800' },
  FinalApproved:{ label: 'Final Approved',  cls: 'bg-emerald-500 text-white' },
  Rejected:     { label: 'Rejected',        cls: 'bg-red-100 text-red-800' },
  Draft:        { label: 'Draft',           cls: 'bg-slate-100 text-slate-700' },
};

export default function DivisionDashboardPage() {
  const { user, formResponses, advanceApproval, countermeasures } = useHGS();
  const navigate = useNavigate();

  // Resolve the user's division metadata from ORG_TREE
  const divisionMeta = useMemo(() => {
    if (!user?.division) return null;
    const oh = ORG_TREE[user.operatingHead];
    if (!oh) return null;
    const div = oh.divisions[user.division];
    if (!div) return null;
    const depts = Object.values(div.departments);
    return { oh, div, depts };
  }, [user]);

  // All submissions whose source department lives under this Division
  const divisionSubs = useMemo(() => {
    if (!divisionMeta) return [];
    const deptIds = new Set(divisionMeta.depts.map((d) => d.id));
    return Object.entries(formResponses || {})
      .map(([k, v]) => ({ key: k, ...v }))
      .filter((r) => r.workflowStatus && r.workflowStatus !== 'Draft')
      .filter((r) => {
        const deptId = resolveDeptId(r.submittedDepartment);
        return deptIds.has(deptId);
      });
  }, [divisionMeta, formResponses]);

  // Action queue: items waiting on the Division Head
  const awaitingMe = useMemo(
    () => divisionSubs.filter((s) => s.workflowStatus === 'Submitted' || s.workflowStatus === 'UnderReview'),
    [divisionSubs],
  );
  const forwarded = useMemo(
    () => divisionSubs.filter((s) => s.workflowStatus === 'Approved'),
    [divisionSubs],
  );
  const finalised = useMemo(
    () => divisionSubs.filter((s) => s.workflowStatus === 'FinalApproved'),
    [divisionSubs],
  );
  const rejected = useMemo(
    () => divisionSubs.filter((s) => s.workflowStatus === 'Rejected'),
    [divisionSubs],
  );

  // Per-department rollup
  const deptRollup = useMemo(() => {
    if (!divisionMeta) return [];
    const map = {};
    divisionMeta.depts.forEach((d) => {
      map[d.id] = { dept: d, submitted: 0, underReview: 0, forwarded: 0, finalised: 0, rejected: 0, total: 0 };
    });
    divisionSubs.forEach((s) => {
      const did = resolveDeptId(s.submittedDepartment);
      if (!map[did]) return;
      map[did].total += 1;
      if (s.workflowStatus === 'Submitted')    map[did].submitted += 1;
      if (s.workflowStatus === 'UnderReview')  map[did].underReview += 1;
      if (s.workflowStatus === 'Approved')     map[did].forwarded += 1;
      if (s.workflowStatus === 'FinalApproved')map[did].finalised += 1;
      if (s.workflowStatus === 'Rejected')     map[did].rejected += 1;
    });
    return Object.values(map);
  }, [divisionMeta, divisionSubs]);

  // Overdue countermeasures scoped to this division
  const overdueCms = useMemo(() => {
    if (!divisionMeta) return [];
    const deptIds = new Set(divisionMeta.depts.map((d) => d.id));
    return (countermeasures || []).filter((c) => {
      const lin = c.department ? lineageForDept(resolveDeptId(c.department)) : null;
      if (!lin || lin.divId !== divisionMeta.div.id) return false;
      const due = c.dueDate || c.targetDate;
      if (!due) return false;
      const days = Math.floor((new Date() - new Date(due)) / 86400_000);
      return c.status !== 'Closed' && days > 0;
    });
  }, [divisionMeta, countermeasures]);

  if (!divisionMeta) {
    return (
      <div>
        <TopBar title="Division Dashboard" breadcrumbs={['HGS', 'Division Dashboard']} />
        <div className="p-8">
          <div className="bg-white rounded-xl border border-amber-200 p-6 flex items-start gap-4">
            <Lock className="w-5 h-5 text-amber-700" />
            <div>
              <h3 className="text-base font-bold text-slate-900">No division mapped to this user</h3>
              <p className="text-sm text-slate-600 mt-1">Your account is not assigned to a division. Contact the GRC Secretariat.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { oh, div, depts } = divisionMeta;

  const handleApprove = (sub) => {
    advanceApproval(sub.key, 'approve-division', 'Division review passed');
    toast.success('Division approved · forwarded to Operating Head');
  };
  const handleReject = (sub) => {
    advanceApproval(sub.key, 'reject', 'Returned for rework');
    toast.error('Submission rejected · returned to department');
  };

  return (
    <div>
      <TopBar
        title={`Division Head Dashboard · ${div.label}`}
        subtitle={`${oh.label} domain · Reviewing submissions from ${depts.length} departments`}
        breadcrumbs={['HGS', 'Division Dashboard']}
      />

      <div className="p-8 space-y-5">
        {/* Identity banner */}
        <div
          className="flex items-center gap-4 p-5 rounded-xl bg-gradient-to-r from-slate-900 to-[#0a1830] text-white"
          data-testid="div-identity-banner"
        >
          <div
            className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center"
            style={{ borderLeft: `3px solid ${oh.color}` }}
          >
            <Layers className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: oh.color }}>
              DIVISIONAL HEAD · REVIEW &amp; APPROVE
            </div>
            <div className="text-base font-bold">{div.label} — Review Workspace</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              You review submissions from your departments and forward approved items to the Operating Head for final sign-off.
            </div>
          </div>
          <div className="flex gap-2">
            <MiniTile label="Awaiting" value={awaitingMe.length} testId="mini-awaiting" />
            <MiniTile label="Forwarded" value={forwarded.length} testId="mini-forwarded" />
            <MiniTile label="Departments" value={depts.length} testId="mini-departments" />
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Review Queue" value={awaitingMe.length} icon={Inbox} tone="amber" testId="kpi-review-queue" />
          <KPI label="Forwarded to OH" value={forwarded.length} icon={Send} tone="sky" testId="kpi-forwarded" />
          <KPI label="Final Approved" value={finalised.length} icon={CheckCircle2} tone="emerald" testId="kpi-final-approved" />
          <KPI label="Rejected" value={rejected.length} icon={AlertTriangle} tone="red" testId="kpi-rejected" />
          <KPI label="Overdue CMs" value={overdueCms.length} icon={Clock} tone="violet" testId="kpi-overdue-cms" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr,420px] gap-5">
          {/* Review Queue */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="review-queue-card">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <Inbox className="w-4 h-4 text-amber-600" />
                  Review Queue
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5">Department submissions awaiting your decision</p>
              </div>
              <button
                onClick={() => navigate('/approvals')}
                className="text-[11px] font-bold text-slate-600 hover:text-[#CC0000] inline-flex items-center gap-1"
                data-testid="open-full-approvals"
              >
                Open full approvals
                <ArrowUpRight className="w-3 h-3" />
              </button>
            </div>

            {awaitingMe.length === 0 ? (
              <div className="p-12 text-center" data-testid="review-queue-empty">
                <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300 mb-3" />
                <p className="text-sm font-bold text-slate-700">Inbox zero — no submissions awaiting review</p>
                <p className="text-xs text-slate-500 mt-1">Department Heads will route Fraud / PCC / DLC submissions here for division-level review.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto">
                {awaitingMe.map((s) => (
                  <ReviewRow
                    key={s.key}
                    sub={s}
                    onApprove={() => handleApprove(s)}
                    onReject={() => handleReject(s)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Department roll-up */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="dept-rollup-card">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-500" />
                Department Submissions
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">Live submission counts per department under {div.label}</p>
            </div>
            <div className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto">
              {deptRollup.map(({ dept, total, submitted, underReview, forwarded, finalised, rejected }) => (
                <div key={dept.id} className="p-4" data-testid={`dept-rollup-${dept.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-7 h-7 rounded-md bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                        {dept.head.avatar}
                      </span>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-slate-900 truncate">{dept.label}</div>
                        <div className="text-[10px] text-slate-500 truncate">{dept.head.name}</div>
                      </div>
                    </div>
                    <span className="text-base font-bold tabular-nums text-slate-900">{total}</span>
                  </div>
                  <div className="grid grid-cols-5 gap-1 text-center">
                    <RollupStat label="Submitted" value={submitted} tone="blue" />
                    <RollupStat label="Review" value={underReview} tone="amber" />
                    <RollupStat label="To OH" value={forwarded} tone="sky" />
                    <RollupStat label="Final" value={finalised} tone="emerald" />
                    <RollupStat label="Rejected" value={rejected} tone="red" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Approval Actions — recent decisions on this division's submissions */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="approval-actions-card">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              Approval Actions
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Recent decisions on submissions from {div.label} departments</p>
          </div>

          {forwarded.length + finalised.length + rejected.length === 0 ? (
            <div className="p-10 text-center" data-testid="approval-actions-empty">
              <p className="text-sm text-slate-500">No approval actions yet on this division&apos;s submissions.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
              {[...forwarded, ...finalised, ...rejected]
                .sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0))
                .slice(0, 20)
                .map((s) => {
                  const M = MODULE_STYLES[s.moduleKey] || MODULE_STYLES.fraud;
                  const tone = STATUS_TONE[s.workflowStatus] || STATUS_TONE.Draft;
                  const last = (s.history || []).slice(-1)[0];
                  return (
                    <div key={s.key} className="px-5 py-3 flex items-center gap-3" data-testid={`action-row-${s.key}`}>
                      <div className={cn('w-8 h-8 rounded-md flex items-center justify-center border', M.bg, M.text, M.border)}>
                        <M.icon className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[10px] text-slate-500">{s.recordId}</span>
                          <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-bold uppercase', M.bg, M.text)}>{M.label}</span>
                          <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-bold', tone.cls)}>{tone.label}</span>
                        </div>
                        <p className="text-[11px] text-slate-600 mt-0.5 truncate">
                          {last ? `${last.action} by ${last.by}` : `Submitted by ${s.submittedBy}`}
                        </p>
                      </div>
                      <span className="text-[10px] text-slate-400 flex-shrink-0">
                        {last ? new Date(last.at).toLocaleDateString() : new Date(s.submittedAt).toLocaleDateString()}
                      </span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ sub, onApprove, onReject }) {
  const M = MODULE_STYLES[sub.moduleKey] || MODULE_STYLES.fraud;
  const tone = STATUS_TONE[sub.workflowStatus] || STATUS_TONE.Draft;
  const deptLineage = sub.submittedDepartment ? lineageForDept(resolveDeptId(sub.submittedDepartment)) : null;

  return (
    <div className="p-4 flex items-start gap-3 hover:bg-slate-50 transition" data-testid={`review-row-${sub.key}`}>
      <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border', M.bg, M.text, M.border)}>
        <M.icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-bold uppercase', M.bg, M.text)}>{M.label}</span>
          <span className="font-mono text-[10px] text-slate-500">{sub.recordId}</span>
          <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-bold', tone.cls)}>{tone.label}</span>
          {deptLineage && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-bold">
              <Building2 className="w-2.5 h-2.5" />
              {deptLineage.deptLabel}
            </span>
          )}
        </div>
        <p className="text-sm font-semibold text-slate-900">Submitted by {sub.submittedBy}</p>
        <p className="text-[11px] text-slate-500">
          {new Date(sub.submittedAt).toLocaleString()} · Response: <span className="font-mono font-bold text-slate-700">{sub.values?.responseType || '—'}</span>
        </p>
      </div>
      <div className="flex flex-col gap-1.5 flex-shrink-0">
        <button
          data-testid={`review-approve-${sub.key}`}
          onClick={onApprove}
          className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold inline-flex items-center gap-1"
        >
          <CheckCircle2 className="w-3 h-3" /> Approve
        </button>
        <button
          data-testid={`review-reject-${sub.key}`}
          onClick={onReject}
          className="px-3 py-1.5 rounded-md bg-white hover:bg-red-50 border border-red-200 text-red-700 text-[11px] font-bold inline-flex items-center gap-1"
        >
          <AlertTriangle className="w-3 h-3" /> Reject
        </button>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-1" />
    </div>
  );
}

function MiniTile({ label, value, testId }) {
  return (
    <div className="text-center px-3 py-2 rounded-lg bg-white/10 backdrop-blur min-w-[72px]" data-testid={testId}>
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-300">{label}</div>
      <div className="text-lg font-bold text-white tabular-nums">{value}</div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, tone = 'slate', testId }) {
  const toneMap = {
    slate:   'bg-slate-50 text-slate-700 border-slate-200',
    amber:   'bg-amber-50 text-amber-800 border-amber-200',
    sky:     'bg-sky-50 text-sky-800 border-sky-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    red:     'bg-red-50 text-red-800 border-red-200',
    violet:  'bg-violet-50 text-violet-800 border-violet-200',
  };
  return (
    <div className={cn('rounded-xl border p-4', toneMap[tone])} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 opacity-60" />}
      </div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function RollupStat({ label, value, tone = 'slate' }) {
  const toneMap = {
    slate:   'text-slate-700',
    blue:    'text-blue-700',
    amber:   'text-amber-700',
    sky:     'text-sky-700',
    emerald: 'text-emerald-700',
    red:     'text-red-700',
  };
  return (
    <div className="px-1.5 py-1 rounded-md bg-slate-50">
      <div className={cn('text-sm font-bold tabular-nums', toneMap[tone])}>{value}</div>
      <div className="text-[8px] font-bold uppercase text-slate-500 tracking-wider">{label}</div>
    </div>
  );
}
